import { Button } from "@/components/ui/button";
import { Heart, Shield, Sparkles, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { WaitlistDialog } from "@/components/WaitlistDialog";
import heroBg from "@/assets/hero-bg.jpg";

const Hero = () => {
  const { t } = useTranslation();
  
  return (
    <section className="relative min-h-[100svh] flex items-center justify-center overflow-hidden pt-20">
      {/* Enhanced Background with overlay - matching ComingSoon */}
      <div className="absolute inset-0 z-0">
        <img 
          src={heroBg} 
          alt="" 
          width={1920}
          height={1080}
          loading="eager"
          decoding="async"
          fetchPriority="high"
          sizes="100vw"
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background" />
      </div>

      {/* Animated Geometric Pattern Overlay */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, hsl(var(--primary)) 35px, hsl(var(--primary)) 36px)`,
        }} />
      </div>

      {/* Glow Effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[128px] animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/20 rounded-full blur-[128px] animate-pulse delay-1000" />

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-5xl mx-auto text-center stagger-children">
          {/* Badge */}
          <div className="inline-flex items-center gap-3 px-6 sm:px-8 py-3 rounded-full bg-primary/10 border-2 border-primary/50 mb-6 sm:mb-8 animate-fade-in shadow-glow backdrop-blur-sm">
            <Sparkles className="w-5 h-5 text-primary animate-pulse" />
            <span className="text-lg sm:text-xl font-bold text-primary">
              {t('hero.badge')}
            </span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-extrabold mb-6 sm:mb-8 bg-gradient-accent bg-clip-text text-transparent leading-[1.1] px-2 animate-fade-in">
            AI Bots Date For You
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl md:text-2xl text-foreground/80 mb-8 sm:mb-10 max-w-3xl mx-auto px-4 leading-relaxed animate-fade-in font-medium">
            {t('hero.subtitle')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 sm:mb-16 animate-fade-in">
            <WaitlistDialog 
              trigger={
                <Button 
                  size="lg" 
                  className="group h-14 px-8 bg-gradient-accent hover:opacity-90 text-white font-semibold rounded-xl shadow-soft hover:shadow-glow transition-all hover:scale-[1.02] active:scale-[0.98] text-lg"
                >
                  {t('hero.startJourney')}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              }
            />
          </div>

          {/* Trust Indicators - Enhanced Cards matching ComingSoon */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto">
            <div className="group relative p-6 rounded-2xl bg-gradient-card backdrop-blur-xl border border-border/50 shadow-soft hover:shadow-glow transition-all hover:scale-[1.02] hover:border-primary/30">
              <div className="absolute inset-0 rounded-2xl bg-gradient-accent opacity-0 group-hover:opacity-5 transition-opacity" />
              <div className="relative flex flex-col items-center gap-3 text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <span className="text-sm font-medium text-foreground">{t('hero.botSimulation')}</span>
              </div>
            </div>
            
            <div className="group relative p-6 rounded-2xl bg-gradient-card backdrop-blur-xl border border-border/50 shadow-soft hover:shadow-glow transition-all hover:scale-[1.02] hover:border-accent/30">
              <div className="absolute inset-0 rounded-2xl bg-gradient-accent opacity-0 group-hover:opacity-5 transition-opacity" />
              <div className="relative flex flex-col items-center gap-3 text-center">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                  <Heart className="w-6 h-6 text-accent" />
                </div>
                <span className="text-sm font-medium text-foreground">{t('hero.aiAnalysis')}</span>
              </div>
            </div>
            
            <div className="group relative p-6 rounded-2xl bg-gradient-card backdrop-blur-xl border border-border/50 shadow-soft hover:shadow-glow transition-all hover:scale-[1.02] hover:border-secondary/30">
              <div className="absolute inset-0 rounded-2xl bg-gradient-accent opacity-0 group-hover:opacity-5 transition-opacity" />
              <div className="relative flex flex-col items-center gap-3 text-center">
                <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                  <Sparkles className="w-6 h-6 text-secondary" />
                </div>
                <span className="text-sm font-medium text-foreground">{t('hero.fullInsights')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-fade-in" style={{ animationDelay: '1s' }}>
        <span className="text-xs text-muted-foreground font-medium tracking-wider uppercase">Scroll to explore</span>
        <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex justify-center pt-2">
          <div className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
